import { createSlice } from '@reduxjs/toolkit';
import { AppThunk } from '@state/store';
import { UserV1, WorkspaceV1 } from '@utils/api/cd4pe';
import { selectWorkspaces } from './selectors';

interface SessionState {
  user: UserV1 | null;
  workspaces: WorkspaceV1[];
  currentWorkspace: WorkspaceV1 | null;
  initialized: boolean;
  currentWorkspacePending: boolean;
}

const initialState: SessionState = {
  user: null,
  workspaces: [],
  initialized: false,
  currentWorkspacePending: true,
  currentWorkspace: null,
};

type BoolAction = { payload: boolean };
type UserAction = { payload: UserV1 };
type WorkspacesAction = { payload: WorkspaceV1[] };
type WorkspaceAction = { payload: WorkspaceV1 };
type RenameWorkspaceAction = {
  payload: { id: string; name: string };
};
type RemoveWorkspaceAction = { payload: string };
type CreateWorkspaceAction = { payload: WorkspaceV1 };

const slice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    resetSession: () => initialState,
    setInitialized: (state: SessionState, action: BoolAction) => {
      state.initialized = action.payload;
    },
    setUser: (state: SessionState, action: UserAction) => {
      state.user = action.payload;
    },
    setWorkspaces: (state: SessionState, action: WorkspacesAction) => {
      state.workspaces = action.payload;
    },
    setCurrentWorkspace: (state: SessionState, action: WorkspaceAction) => {
      state.currentWorkspace = action.payload;
      state.currentWorkspacePending = false;
    },
    renameWorkspace: (state: SessionState, actions: RenameWorkspaceAction) => {
      state.workspaces = state.workspaces.map((w) => ({
        ...w,
        name: w.domain === actions.payload.id ? actions.payload.name : w.name,
      }));
    },
    removeWorkspace(state: SessionState, action: RemoveWorkspaceAction) {
      state.workspaces = state.workspaces.filter(
        (w) => w.domain !== action.payload,
      );
    },
    createWorkspace(state: SessionState, action: CreateWorkspaceAction) {
      state.workspaces = [...state.workspaces, action.payload];
      state.currentWorkspace = action.payload;
      state.currentWorkspacePending = false;
    },
  },
});

export const sessionReducer = slice.reducer;

const { actions } = slice;

export const initializeApp =
  (
    user: UserV1,
    workspaces: Array<WorkspaceV1> = [],
  ): AppThunk<Promise<void>> =>
  async (dispatch) => {
    dispatch(actions.setUser(user));
    dispatch(actions.setWorkspaces(workspaces));
    dispatch(actions.setInitialized(true));
  };

export const setCurrentWorkspace =
  (workspaceName: WorkspaceV1['name']): AppThunk =>
  (dispatch, getState) => {
    if (workspaceName === 'root') {
      const rootWs = { name: 'root', domain: 'root' } as WorkspaceV1;
      dispatch(actions.setCurrentWorkspace(rootWs));
      return;
    }
    const workspaces = selectWorkspaces(getState());
    const workspace =
      workspaces.find(
        (w: WorkspaceV1) =>
          w.name &&
          workspaceName &&
          w.name.toLowerCase() === workspaceName.toLowerCase(),
      ) || workspaces[0];

    dispatch(actions.setCurrentWorkspace(workspace || null));
  };

export default {
  actions,
  setCurrentWorkspace,
  initializeApp,
};
